import { useEffect, useState } from "react";

import { Link } from "react-router-dom";
import { timeRangeOpts } from "@doitintl/clouddiagrams-frontend/app/context/Costs";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import Grid from "@mui/material/Grid2";
import InputAdornment from "@mui/material/InputAdornment";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { type SxProps } from "@mui/system";

import { useApiContext } from "../../../api/context";
import { useCustomerContext } from "../../../Context/CustomerContext";
import { consoleErrorWithSentry } from "../../../utils";
import { useFullScreen } from "../../../utils/dialog";
import useSegmentTrackEvent from "../../../utils/useSegmentTrackEvent";
import cdAppSettings from "../cdAppSetting";

interface DiagramData {
  scheme_id: string;
  statussheet_id: string;
}

interface SchemeResponse {
  scheme?: {
    [key: string]: {
      statussheet?: Array<{ ssid: string }>;
    };
  };
}

const NetworkDiagramButton = ({
  sx,
  disabled,
  ...props
}: {
  sx?: SxProps;
  disabled?: boolean;
  [key: string]: unknown;
}) => {
  const [networkDiagram, setNetworkDiagram] = useState<DiagramData | undefined>();

  const { customer } = useCustomerContext();
  const api = useApiContext();

  useEffect(() => {
    const fetchNetworkDiagram = async () => {
      if (!customer?.id) {
        consoleErrorWithSentry("Missing customer");
        return;
      }

      try {
        const { data } = await api.request<SchemeResponse>({
          method: "post",
          baseURL: cdAppSettings.baseUrl,
          url: "/cloud-diagrams/api/scheme/get",
          params: {
            customer: customer.id,
            "type[]": "network",
          },
        });

        const scheme_id = Object.keys(data?.scheme || {})[0];
        const statussheet_id = data?.scheme?.[scheme_id]?.statussheet?.[0]?.ssid;

        if (scheme_id && statussheet_id) {
          setNetworkDiagram({ scheme_id, statussheet_id });
        }
      } catch (err) {
        consoleErrorWithSentry(err);
      }
    };

    fetchNetworkDiagram();
  }, [api, customer]);

  return (
    <Button
      component="a"
      variant="outlined"
      color="primary"
      sx={{ height: 40, ...sx }}
      disabled={!!disabled || !networkDiagram}
      href={
        networkDiagram
          ? `/cloud-diagrams/network/${networkDiagram.scheme_id}/${networkDiagram.statussheet_id}/${customer?.id}`
          : "#"
      }
      size="large"
      data-cy="clouddiagrams-view-network-layer"
      {...props}
    >
      View network layer
    </Button>
  );
};

let debounceTimer: NodeJS.Timeout;

const DashboardHead = ({ onSearch, isCostTrendingLoading, timeRange, setTimeRange }) => {
  const [searchQuery, setSearchQuery] = useState<string>("");

  const { customer } = useCustomerContext();
  const { trackEvent } = useSegmentTrackEvent();
  const { isMobile } = useFullScreen();

  const handleCreateDiagramClick = () => {
    trackEvent("CloudDiagrams.Client DiagramCreationStarted", {
      feature: "CloudDiagrams",
    });
  };

  const handleViewNetworkLayerClick = () => {
    trackEvent("CloudDiagrams.Client ViewNetworkLayerClicked", {
      feature: "CloudDiagrams",
    });
  };

  useEffect(() => {
    clearTimeout(debounceTimer);

    debounceTimer = setTimeout(() => {
      onSearch(searchQuery.trim().toLowerCase());
    }, 300);

    return () => {
      clearTimeout(debounceTimer);
    };
  }, [onSearch, searchQuery]);

  return (
    <Grid
      container
      spacing={1}
      sx={{
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <Grid sx={{ paddingTop: "1rem" }}>
        <Typography
          data-cy="clouddiagrams-title"
          variant="h1"
          sx={{
            mb: 1,
          }}
        >
          Cloud diagrams
        </Typography>
        <Typography
          data-cy="clouddiagrams-description"
          variant="body1"
          color="textSecondary"
          sx={{
            fontWeight: 400,
            mb: 2,
          }}
        >
          Visualize and analyze your cloud infrastructure
        </Typography>
      </Grid>
      <Grid
        container
        sx={
          isMobile
            ? { width: "100%", flexDirection: "column-reverse", gap: 3, mb: 1.5 }
            : {
                alignItems: "center",
                gap: 1,
              }
        }
      >
        <TextField
          data-cy="clouddiagrams-dashboard-search"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
          }}
          placeholder="Search diagrams"
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlinedIcon />
                </InputAdornment>
              ),
            },
          }}
          variant="outlined"
        />
        {isMobile && (
          <Alert severity="info">Cloud Diagrams is unavailable on mobile screens. Please use a desktop device.</Alert>
        )}
        <Box sx={isMobile ? { display: "flex", gap: 2 } : undefined}>
          <FormControl sx={isMobile ? { mx: 0, flex: "1 1 160px", minWidth: "160px" } : { mx: 1, minWidth: "160px" }}>
            <InputLabel>Time range</InputLabel>
            <Select
              value={timeRange}
              onChange={(e) => {
                setTimeRange(Number(e.target.value));
              }}
              size="small"
              label="Time range"
              disabled={isCostTrendingLoading}
            >
              {timeRangeOpts.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.text}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <NetworkDiagramButton
            disabled={isMobile}
            sx={isMobile ? { marginLeft: 0, flex: "1 1 0", minWidth: "10px" } : { marginLeft: 0.5 }}
            onClick={handleViewNetworkLayerClick}
          />
          <Button
            data-cy="clouddiagrams-dashboard-creatediagram"
            variant="contained"
            color="primary"
            disabled={isMobile}
            sx={isMobile ? { flex: "1 1 0", minWidth: "10px" } : { marginLeft: 1.5 }}
            component={Link}
            to={`/customers/${customer.id}/analyze/cloud-diagrams/onboarding`}
            size="large"
            onClick={handleCreateDiagramClick}
          >
            Create diagram
          </Button>
        </Box>
      </Grid>
    </Grid>
  );
};

export default DashboardHead;
